import { useCallback, useMemo } from 'react'

import { NewSearchData, SearcherInfo } from '../../../models'
import { useFlags } from '../../flags/useFlags'
import { AiInTriageThreeArmVariation, PrimaryNeedsWords } from '../constants'
import { cleanCompletedSearchForDuplication, CleanCompletedSearchForDuplicationArgs } from '../utils'

type useBackToTriage = {
  goBack?: () => void
  searcherInfo?: SearcherInfo | null
  isCreatingSearch: boolean
  postTriageSearch: (newSearchData: NewSearchData) => Promise<any>
  shouldCreateDuplicateInProgressSearch?: boolean
  primaryNeedsWord?: string[] | PrimaryNeedsWords[]
} & Omit<CleanCompletedSearchForDuplicationArgs, 'memberAge' | 'searcherType' | 'primaryNeedsWord'>

export const useBackToTriage = ({
  goBack,
  search,
  searcherInfo,
  isCreatingSearch,
  postTriageSearch,
  shouldCreateDuplicateInProgressSearch = true,
  getWordCloudWords,
  wordCloudWords,
  primaryNeedsWord,
  country,
  lyraSocialCareEnabled,
  fetchedMetadataVersion,
}: useBackToTriage) => {
  const { canSelectMultiplePrimaryNeeds, isAiInTriageEnabledThreeArm } = useFlags()
  const primaryNeeds = useMemo(() => {
    if (primaryNeedsWord) {
      return canSelectMultiplePrimaryNeeds ||
        isAiInTriageEnabledThreeArm === AiInTriageThreeArmVariation.FREE_TEXT_ONLY ||
        isAiInTriageEnabledThreeArm === AiInTriageThreeArmVariation.FREE_TEXT_AI_RESPONSES
        ? primaryNeedsWord
        : primaryNeedsWord[0]
    } else {
      return 'N/A'
    }
  }, [canSelectMultiplePrimaryNeeds, isAiInTriageEnabledThreeArm, primaryNeedsWord])
  const handleBackPress = useCallback(async () => {
    // Create a duplicate in_progress search when going back to triage
    goBack ? goBack() : null
    if (!isCreatingSearch && shouldCreateDuplicateInProgressSearch && search && searcherInfo) {
      const newSearchData = await cleanCompletedSearchForDuplication({
        search,
        getWordCloudWords,
        memberAge: searcherInfo.age,
        searcherType: searcherInfo.type,
        country,
        lyraSocialCareEnabled,
        primaryNeedsWord: primaryNeeds,
        wordCloudWords,
        fetchedMetadataVersion,
      })
      await postTriageSearch(newSearchData)
    }
  }, [
    country,
    fetchedMetadataVersion,
    getWordCloudWords,
    goBack,
    isCreatingSearch,
    lyraSocialCareEnabled,
    postTriageSearch,
    primaryNeeds,
    search,
    searcherInfo,
    shouldCreateDuplicateInProgressSearch,
    wordCloudWords,
  ])

  return handleBackPress
}
